import React, { useEffect, useState } from "react";

import { useDispatch, useSelector } from "react-redux";

import { Card, Col, Container, Row, Table } from "react-bootstrap";

import {
  useGenerateReportsMutation,
  useGetTrackersQuery,
} from "@/features/trackers/trackersApi";

import {
  resetFilters,
  resetSecondaryFilters,
  setPrimaryFilters,
  setSecondaryFilters,
  setSort,
} from "@/features/filters/filtersSlice";

import DateRangePicker from "@/common/DateRangePicker/DateRangePicker";
import FilterSelect from "@/common/FilterSelect/FilterSelect";
import FormItem from "@/common/FormItem/FormItem";
import LoadingOverlay from "@/common/LoadingOverlay/LoadingOverlay";
import PageTitle from "@/common/PageTitle/PageTitle";

import TrackersRow from "./TrackersRow";

import type { DefaultOptionType } from "antd/es/select";

import type {
  InitialFormValues,
  Tracker,
  TrackersFilterTypeIds,
  TrackersFilterTypes,
} from "@/features/trackers/types";

import {
  selectInputs,
  TrackersFilterTypeIdMap,
} from "@/features/trackers/constants";

import {
  CaretDownOutlined,
  CloudDownloadOutlined,
  FilterOutlined,
  LoadingOutlined,
} from "@ant-design/icons";

import { Form } from "antd";

import dayjs from "dayjs";

import toast from "react-hot-toast";

import BrandButton from "@/common/BrandButton/BrandButton";
import TableRowNoResults from "@/common/TableRowNoResults/TableRowNoResults";
import PaginationRow from "@/components/PaginationRow/PaginationRow";
import { getEntries } from "@/utils/helpers";

interface OptionType extends DefaultOptionType {
  type?: string;
}
const initialFormValues: InitialFormValues = {
  songs: null,
  channels: null,
  master_accounts: null,
  zones: null,
  audio_types: null,
  messages: null,
  playTimeStart: null,
  playTimeEnd: null,
};

const tableHeaders = [
  { name: "Master Account", key: "master_account" },
  { name: "Zone Username", key: "zone" },
  { name: "Audio", key: "audio_type" },
  { name: "Song", key: "song" },
  { name: "Play Time", key: "play_time" },
  { name: "Channel", key: "channel", minWidth: 220, width: 240, maxWidth: 260, style: { whiteSpace: 'nowrap' } },
];

const TrackersList: React.FC = () => {
  const dispatch = useDispatch();

  const [page, setPage] = useState<number>(1);

  const [form] = Form.useForm<InitialFormValues>();

  const primaryFilters = useSelector(
    (state: any) => state.filters.primaryFilters
  );

  const secondaryFilters = useSelector(
    (state: any) => state.filters.secondaryFilters
  );

  const sort = useSelector((state: any) => state.filters.sort);

  const {
    data: trackersData,
    isFetching: isFetchingTrackers,
    isLoading: isLoadingTrackers,
    isError: isTrackersError,
  } = useGetTrackersQuery({ params: { sort, ...primaryFilters }, page });

  // No mock fallback: the Licensing backend does not exist yet, so an API
  // failure means "not yet connected" — never render fabricated rows.
  const trackers: Tracker[] = trackersData?.items ?? [];
  const isTrackersUnavailable =
    isTrackersError && !isFetchingTrackers && !isLoadingTrackers;

  const [generateReports, { isLoading: isSubmittingReport, isSuccess, error }] =
    useGenerateReportsMutation();

  useEffect(() => {
    if (isSuccess) {
      toast.success("Report submitted");
    } else if (error) {
      toast.error("Failed to submit report");
    }
  }, [isSuccess, error]);

  const getTransformedFormFields = (formValues: InitialFormValues) => {
    const formEntries = getEntries(formValues);

    return formEntries.map(([filterType, value]) => [
      TrackersFilterTypeIdMap[
        filterType as keyof typeof TrackersFilterTypeIdMap
      ] || undefined,
      value,
      filterType,
    ]) as [
      Partial<TrackersFilterTypeIds>,
      string,
      Partial<TrackersFilterTypes>
    ][];
  };

  const handleOnSelectFilterOption = (_: string, option: OptionType) => {
    if (option.type === undefined) return;

    const selectedOptionType = option.type;

    const transformedFormFields = getTransformedFormFields(
      form.getFieldsValue(true, (meta) => meta.touched)
    );

    const mappedFormValues = transformedFormFields.reduce((acc, key) => {
      const [filterTypeId, value, filterType] = key;

      const isSelectedOptionType = selectedOptionType === filterType;

      if (filterTypeId === undefined || value === null || value === undefined) {
        return acc;
      }

      if (isSelectedOptionType && filterType === "songs") {
        const { messageId, ...newObject } = acc;

        const { fuzz, ...newSecondaryFilters } = secondaryFilters;

        acc = newObject;
        acc.audioType = "Song";
        acc[filterTypeId] = value;

        form.setFieldValue("audio_types", "Song");
        form.setFieldValue("messages", null);

        setSecondaryFilters(newSecondaryFilters);
      } else if (isSelectedOptionType && filterType === "messages") {
        const { songId, ...newObject } = acc;

        acc = newObject;
        acc.audioType = "Message";
        acc[filterTypeId] = value;

        form.setFieldValue("audio_types", "Message");
        form.setFieldValue("songs", null);
      } else if (isSelectedOptionType && filterType === "master_accounts") {
        const { messageId, zoneId, ...newObject } = acc;

        acc = newObject;
        acc[filterTypeId] = value;

        form.setFieldValue("zones", null);
        form.setFieldValue("messages", null);

        dispatch(setSecondaryFilters({ masterAccountId: value }));
      } else if (isSelectedOptionType && filterType === "audio_types") {
        const filterToRemove =
          value === "Song"
            ? { key: "messageId", value: "messages" }
            : { key: "songId", value: "songs" };

        const { [filterToRemove.key as keyof typeof acc]: _, ...newObject } =
          acc;

        acc = newObject;
        acc[filterTypeId] = value;

        form.setFieldValue(filterToRemove.value as any, null);
      } else {
        acc[filterTypeId] = value;
      }

      return acc;
    }, {} as Partial<Record<TrackersFilterTypeIds, string | null>>);

    dispatch(setPrimaryFilters(mappedFormValues));

    setPage(1);
  };

  const handleDateChange = (dates: any) => {
    if (dates) {
      const [playTimeStart, playTimeEnd] = dates;
      form.setFieldsValue({
        playTimeStart: playTimeStart.utc().format("YYYY-MM-DD"),
        playTimeEnd: playTimeEnd.utc().format("YYYY-MM-DD"),
      });
    } else {
      form.setFieldsValue({
        playTimeStart: null,
        playTimeEnd: null,
      });
    }

    const transformedFormFields = getTransformedFormFields(
      form.getFieldsValue(true)
    );

    const updatedPrimaryFilters = transformedFormFields.reduce((acc, key) => {
      const [filterTypeId, value] = key;

      if (filterTypeId !== undefined && value !== null && value !== undefined) {
        acc[filterTypeId] = value;
      }

      return acc;
    }, {} as Partial<Record<TrackersFilterTypeIds, string | null>>);

    dispatch(setPrimaryFilters(updatedPrimaryFilters));

    setPage(1);
  };

  const handleOnClearInput = (name: TrackersFilterTypes) => {
    const filterId = TrackersFilterTypeIdMap[name];

    let { ...updatedPrimaryFilters } = primaryFilters;

    updatedPrimaryFilters = Object.keys(updatedPrimaryFilters).reduce(
      (acc: any, key: any) => {
        const keysToRemove: string[] = [filterId];
        switch (filterId) {
          case "songId":
            keysToRemove.push("messageId");
            form.setFieldValue("audio_types", null);
            break;
          case "messageId":
            keysToRemove.push("audioType");
            form.setFieldValue("audio_types", null);
            break;
          case "audioType":
            keysToRemove.push("messageId");
            form.setFieldValue("messages", null);
            break;
          case "masterAccountId":
            keysToRemove.push("zoneId", "messageId");
            form.setFieldValue("zones", null);
            form.setFieldValue("messages", null);
            dispatch(resetSecondaryFilters());
            break;
          default:
            break;
        }

        if (keysToRemove.includes(key) === false) {
          acc[key] = primaryFilters[key];
        }

        return acc;
      },
      {} as Partial<Record<TrackersFilterTypeIds, string | null>>
    );

    dispatch(setPrimaryFilters(updatedPrimaryFilters));

    setPage(1);
  };

  const handleResetFilters = () => {
    form.resetFields();
    dispatch(resetFilters());
    setPage(1);
  };

  const handleOnSumbit = () => {
    const {
      songs: songId,
      channels: channelId,
      master_accounts: masterAccountId,
      zones: zoneId,
      audio_types: audioType,
      messages: messageId,
      playTimeStart,
      playTimeEnd,
    } = form.getFieldsValue(true);

    generateReports({
      songId,
      channelId,
      masterAccountId,
      zoneId,
      audioType,
      messageId,
      playTimeStart,
      playTimeEnd,
    });
  };

  const handleOnClickSort = () => {
    dispatch(setSort(sort === "asc" ? "desc" : "asc"));
  };

  const renderSortIcon = () => {
    if (sort === "asc") {
      return <CaretDownOutlined />;
    }
    return <CaretDownOutlined style={{ transform: "rotate(180deg)" }} />;
  };

  const handleFuzzySearch = (value: string) => {
    let { ...updatedSecondaryFilters } = secondaryFilters;

    updatedSecondaryFilters.fuzz = value;

    dispatch(setSecondaryFilters(updatedSecondaryFilters));
  };

  const handleOnClickNext = () => {
    setPage((currentPage) => currentPage + 1);
  };

  const handleOnClickPrev = () => {
    setPage((currentPage) => Math.max(currentPage - 1, 1));
  };

  return (
    <Container fluid>
      <Form
        requiredMark={false}
        layout="vertical"
        onFinish={handleOnSumbit}
        initialValues={initialFormValues}
        form={form}
      >
        <Card>
          <Card.Body>
            <Card.Title style={{ display: 'flex', justifyContent: 'space-between' }}>
              <PageTitle title="Trackers" />
              <BrandButton
                variant="outline"
                onClick={handleResetFilters}
                disabled={!primaryFilters}
              >
                <FilterOutlined /> Reset All
              </BrandButton>
            </Card.Title>
            {(isLoadingTrackers || isFetchingTrackers) && <LoadingOverlay />}
            <Row>
              <Col>
                <Row className="mb-2">
                  <Col>
                    <Row>
                      {selectInputs.map(({ name, label, placeholder }) => (
                        <Col key={name} md={4}>
                          <FormItem
                            name={name}
                            label={label}
                            className="customSelectWrapper"
                            layout="vertical"
                          >
                            <FilterSelect<"trackers">
                              onSelect={handleOnSelectFilterOption}
                              name={name}
                              filterOption={name === "songs" ? false : true}
                              allowFuzzySearch={name === "songs"}
                              onSearch={(value: string) =>
                                name === "songs"
                                  ? handleFuzzySearch(value)
                                  : undefined
                              }
                              placeholder={placeholder}
                              filterParams={secondaryFilters}
                              onClear={() => handleOnClearInput(name)}
                            />
                          </FormItem>
                        </Col>
                      ))}
                    </Row>
                  </Col>
                </Row>
                <Row className="mb-2">
                  <Col md={8}>
                    <FormItem
                      name="dateRange"
                      validateTrigger="onFinish"
                      label="Filter by Date Range"
                      layout="vertical"
                      rules={[
                        {
                          required: true,
                          message:
                            "A daterange is required to generate a report",
                        },
                      ]}
                    >
                      <DateRangePicker
                        maxDate={dayjs()}
                        onChange={handleDateChange}
                        style={{ width: '100%' }}
                      />
                    </FormItem>
                  </Col>
                  <Col
                    md={4}
                    className="d-flex align-items-center mt-2 mt-md-0"
                  >
                    <BrandButton
                      variant="outline-primary"
                      type="submit"
                      className="outline w-100"
                      disabled={isSubmittingReport}
                    >
                      {isSubmittingReport ? (
                        <LoadingOutlined />
                      ) : (
                        <CloudDownloadOutlined />
                      )}{" "}
                      Generate Report
                    </BrandButton>
                  </Col>
                </Row>
                <Row>
                  <Col>
                    <Table hover responsive className="legacy-row-height-table">
                      <thead>
                        <tr>
                          {tableHeaders.map((header, index) => (
                            <th
                              style={{
                                cursor:
                                  header.key === "play_time"
                                    ? "pointer"
                                    : "default",
                              }}
                              onClick={
                                header.key === "play_time"
                                  ? handleOnClickSort
                                  : undefined
                              }
                              key={index}
                            >
                              {header.name}{" "}
                              {header.key === "play_time" && renderSortIcon()}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {!isTrackersUnavailable &&
                          trackers.map((tracker, index: number) => (
                            <TrackersRow tracker={tracker} key={index} />
                          ))}
                        {isTrackersUnavailable && (
                          <tr>
                            <td
                              colSpan={tableHeaders.length}
                              className="text-center text-muted py-5"
                            >
                              <div style={{ fontWeight: 600, marginBottom: 4 }}>
                                Trackers is not yet connected
                              </div>
                              <div>
                                Live play-history data will appear here once the
                                Licensing backend is available.
                              </div>
                            </td>
                          </tr>
                        )}
                        {!isTrackersUnavailable && trackers.length === 0 && (
                          <TableRowNoResults colSpan={7} />
                        )}
                      </tbody>
                    </Table>
                  </Col>
                </Row>
                <PaginationRow
                  onClickPrevious={handleOnClickPrev}
                  handleOnClickNext={handleOnClickNext}
                  disableLeft={
                    page === 1 ||
                    isFetchingTrackers ||
                    isLoadingTrackers ||
                    isTrackersUnavailable
                  }
                  disableNext={
                    isFetchingTrackers ||
                    isLoadingTrackers ||
                    isTrackersUnavailable ||
                    trackers.length < 25
                  }
                  page={page}
                  onPageChange={(p) => setPage(p)}
                />
              </Col>
            </Row>
          </Card.Body>
        </Card>
      </Form>
      <style>{`
        .trackers-channel-col {
          padding-left: 0px !important;
          width: 120px !important;
          max-width: 120px !important;
          white-space: nowrap !important;
        }
      `}</style>
    </Container>
  );
};

export default TrackersList;
